<template>
  <div>
    <h3 v-if="person.isAdult" class="heading-3 weight-medium pb-10 pb-md-16 text-left text-md-center">
      Enter your Personal Data
    </h3>
    <h3 v-else class="heading-3 weight-medium pb-10 pb-md-16 text-left text-md-center">
      Enter the Child's Personal Data
    </h3>

    <div class="input-box-holder" v-validate="errors" v-has-value="person.childBornAbroad">
      <DoubleInlineRadioButton
        v-if="showChildBornAbroadSection"
        class="mb-10 weight-regular body-3"
        first-title='Yes'
        second-title='No'
        v-model="person.childBornAbroad"
        :name="'childBornAbroad' + index"
      >
        <h4 class="weight-medium text-center mb-10">
          Was the child born abroad?
        </h4>
      </DoubleInlineRadioButton>
      <div class="validation-output text-left mt-2">
        <ErrorHandler class="error" :id="`V283_${index}`">
          Select one of the options.
        </ErrorHandler>
      </div>
    </div>

    <div class="input-box-holder" v-validate="errors" v-has-value="person.isLegalRepresentativeForAllChildren">
      <DoubleInlineRadioButton
        v-if="showIsLegalRepresentativeForAllChildren"
        class="mb-10 weight-regular body-3"
        first-title='Yes'
        second-title='No'
        @input="setIsLegalRepresentativeForAllChildren"
        :value="person.isLegalRepresentativeForAllChildren"
        :name="'isLegalRepresentativeForAllChildren' + index"
      >
        <h4 class="weight-medium text-center mb-10">
          Are you the Legal Representative of all insured children?
        </h4>
      </DoubleInlineRadioButton>
      <div class="validation-output text-left mt-2">
        <ErrorHandler class="error" :id="`V330_${index}`">
          Select one of the options.
        </ErrorHandler>
      </div>
    </div>

    <SelectField
      v-if="person.isAdult"
      class="mt-8"
      title='Title Before Name'
      :options="enumsValues.TitleInFrontOfNameEnum"
      v-model="person.degreeBeforeName"
      :without-icon="true"
    />

    <div class="input-box-holder mt-8" v-validate="errors" v-has-value="person.name">
      <TextField
        class="input-box"
        title='Name'
        v-model.trim="person.name"
        :id="`name_${index}`"
        required
      />
      <div class="validation-output text-left mt-2">
        <ErrorHandler class="error" :id="`V012_${index}`">
          Enter the name of the policyholder.
        </ErrorHandler>

        <ErrorHandler class="error" :id="`V195_${index}`">
          The minimum number of characters is 2.
        </ErrorHandler>

        <ErrorHandler class="error" :id="`V013_${index}`">
          The name of the policyholder contains invalid characters.
        </ErrorHandler>

        <ErrorHandler class="error" :id="`V133_${index}`">
          The maximum number of characters is 30.
        </ErrorHandler>
      </div>
    </div>

    <div class="input-box-holder mt-8" v-validate="errors" v-has-value="person.surname">
      <TextField
        class="input-box"
        title='Surname'
        v-model.trim="person.surname"
        @blur="setBirthSurname"
        :id="`surname_${index}`"
        required
      />
      <div class="validation-output text-left mt-2">
        <ErrorHandler class="error" :id="`V014_${index}`">
          Enter the surname of the policyholder.
        </ErrorHandler>

        <ErrorHandler class="error" :id="`V196_${index}`">
          The minimum number of characters is 2.
        </ErrorHandler>

        <ErrorHandler class="error" :id="`V015_${index}`">
          The surname of the policyholder contains invalid characters.
        </ErrorHandler>

        <ErrorHandler class="error" :id="`V134_${index}`">
          The maximum number of characters is 30.
        </ErrorHandler>
      </div>
    </div>

    <div class="input-box-handler mt-8" v-validate="errors" v-has-value="person.birthSurname">
      <TextField
        class="input-box"
        title='Birth Name'
        v-model.trim="person.birthSurname"
        :id="`birth-surname_${index}`"
        required
      >
        <Tooltip slot="tooltip">
          <p class="text-left m-0">
            
    <b>The birth name</b> for a married woman or a married man is the surname used prior to marriage
    or acquired at birth.
    <br/>
    <br/>
    We ask this from all policyholders for easier and clearer identification.
  
          </p>
        </Tooltip>
      </TextField>

      <div class="validation-output text-left mt-2">
        <ErrorHandler class="error" :id="`V016_${index}`">
          Enter the birth name of the policyholder.
        </ErrorHandler>

        <ErrorHandler class="error" :id="`V197_${index}`">
          The minimum number of characters is 2.
        </ErrorHandler>

        <ErrorHandler class="error" :id="`V017_${index}`">
          The birth name of the policyholder contains invalid characters.
        </ErrorHandler>

        <ErrorHandler class="error" :id="`V135_${index}`">
          The maximum number of characters is 30.
        </ErrorHandler>
      </div>
    </div>

    <SelectField
      v-if="person.isAdult"
      class="mt-8"
      title='Title Behind Name'
      :options="enumsValues.TitleAfterNameEnum"
      v-model="person.degreeAfterName"
      :without-icon="true"
    />

    <div class="input-box-handler mt-8" v-validate="errors" v-has-value="person.nationality">
      <SelectField
        class="input-box"
        title='Nationality'
        :options="$store.state.countries"
        :value="person.nationality"
        @input="setNationality"
        withAllOptions
        required
        valueOnlyFromOptions
      />
      <div class="validation-output text-left mt-2">
        <ErrorHandler class="error" :id="`V026_${index}`">
          Select the nationality of the policyholder.
        </ErrorHandler>
      </div>
    </div>

    <div v-if="person.isAdult" class="input-box-handler mt-8" v-validate="errors" v-has-value="person.iban">
      <TextField
        class="input-box"
        title="IBAN"
        :id="`iban_${index}`"
        v-model="person.iban"
        v-mask="'AA## #### #### #### #### ####'"
      >
        <Tooltip slot="tooltip">
          <p class="text-left m-0">
            
    <b>IBAN</b> is the standard international bank account number format that we need
    to automatically reimburse health insurance overpayments.
  
          </p>
        </Tooltip>
      </TextField>
      <div class="validation-output text-left mt-2">
        <ErrorHandler class="error" :id="`V028_${index}`">
          Enter IBAN in the correct format.
        </ErrorHandler>
      </div>
    </div>

    <IdentityVerification
      v-if="showIdentityVerificationSection"
      :index="index"
      :person-index="index"
      :isChild="true"
      :person="person"
      :model="model"
      :errors="errors"
      class="mt-8"
    />

    <Checkbox
      v-if="person.isAdult"
      class="mt-10 mb-10 body-1 weight-medium"
      title='The Policyholder Has a Disability (ZŤP)'
      v-model="person.ztp"
    />
    <Checkbox
      v-else
      class="mt-10 mb-10 body-1 weight-medium"
      title='The Child Has a Disability (ZŤP)'
      v-model="person.ztp"
    />
  </div>
</template>
<script>
import Checkbox from '@/components/Checkbox/Checkbox.vue';
import SelectField from '@/components/SelectField/SelectField.vue';
import TextField from '@/components/TextField/TextField.vue';
import { COUNTRY_CODE_FOR_SLOVAKIA } from '@/config';
import persons from '@/mixins/persons';
import enums from '@/mixins/enums';
import ErrorHandler from '@/components/ErrorHandler/ErrorHandler.vue';
import DoubleInlineRadioButton from '@/components/DoubleInlineRadioButton/DoubleInlineRadioButton.vue';
import IdentityVerification from '@/pagesComponents/IdentityVerification.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';

export default {
  mixins: [persons, enums],
  components: {
    IdentityVerification,
    DoubleInlineRadioButton,
    TextField,
    SelectField,
    Checkbox,
    ErrorHandler,
    Tooltip,
  },
  props: {
    person: { type: Object, required: true },
    index: { type: Number, required: true },
    errors: { type: Array, required: true },
    model: { type: Object, required: true },
  },
  mounted() {
    if (!this.person.nationality) {
      this.setNationality({
        area: 'SK',
        areaName: 'Slovenská republika',
        calcKey: '4',
        label: 'Slovenská republika',
        name$: '703',
        optgroup: false,
        search: 'slovenska republika',
        value: 703,
      });
    }
  },
  computed: {
    InsuredPersonType() { return this.enums.InsuredPersonTypeEnum; },
    enumsValues() {
      const locale = process.env.NODE_ENV === 'production' ? process.env.LOCALE : process.env.VUE_APP_I18N_FALLBACK_LOCALE;
      return Object.entries(this.enums)
        .reduce((accumulator, entry) => {
          const [key, value] = entry;
          /*
          * It cannot be written through rest operator,
          * because then we'll remove Kotlin enumeration type and it will be normal JS object.
          * */
          const options = value.values().map((e) => {
            const label = e[locale];
            if (label) e.label = label;
            return e;
          });
          accumulator[key] = options;
          return accumulator;
        }, {});
    },
    showChildBornAbroadSection() {
      return !this.person.isAdult && this.person.insuredPersonType === this.InsuredPersonType.BIRTH_OF_CHILD;
    },
    showIdentityVerificationSection() {
      return this.person.insuredPersonType === this.InsuredPersonType.BIRTH_OF_CHILD
        || this.person.insuredPersonType === this.InsuredPersonType.RETURN_FROM_ABROAD_CHILD
        || this.person.insuredPersonType === this.InsuredPersonType.CHANGE_OF_INSURANCE_COMPANY_CHILD;
    },
    showIsLegalRepresentativeForAllChildren() {
      return this.person.isAdult
        && this.children.length > 0
        && this.countAllAdultPersons < 2;
    },
  },
  methods: {
    setBirthSurname() {
      if (!this.person.birthSurname) {
        this.person.birthSurname = this.person.surname;
      }
    },
    setNationality(country) {
      const { EvidenceTypeEnum, DeliveryTypeEnum } = this.enums;
      this.person.nationality = country;
      if (country?.value === COUNTRY_CODE_FOR_SLOVAKIA) {
        this.person.address.country = country;
        this.person.postalAddress.country = country;
        this.person.temporaryResidence.country = country;
        this.setHasBirthNumber(true);
        // Residence permit is not available option for Slovakia nationality
        // If user has selected residence permit then remove this value from radio-box
        if (this.person.evidenceType === EvidenceTypeEnum.RESIDENCE_PERMIT) this.person.evidenceType = null;
      } else {
        if (this.model.delivery.deliveryType === DeliveryTypeEnum.E_SIGNATURE) {
          this.model.delivery.deliveryType = null;
        }
        this.setHasBirthNumber(true);
      }
    },
    setHasBirthNumber(bool) {
      this.person.hasBirthNumber = bool;
      this.person.birthNumber = '';
      this.person.birthDate = null;
    },
    setIsLegalRepresentativeForAllChildren(bool) {
      this.person.isLegalRepresentativeForAllChildren = bool;
      if (bool) {
        const legalRepresentative = this.insuredPersons[0].firstLegalRepresentative;
        this.setPersonDataFromLegalRepresentative(legalRepresentative);
        this.setLegalRepresentativeIndexForAllChildren(this.index);
      } else {
        this.resetPersonData();
        this.setLegalRepresentativeIndexForAllChildren(-1);
      }
    },
    setPersonDataFromLegalRepresentative(legalRepresentative) {
      this.person.name = legalRepresentative.name;
      this.person.surname = legalRepresentative.surname;
      this.person.birthSurname = this.person.surname;
      this.person.nationality = legalRepresentative.nationality;
      this.person.hasBirthNumber = legalRepresentative.hasBirthNumber;
      this.person.birthNumber = legalRepresentative.birthNumber;
      this.person.birthDate = legalRepresentative.birthDate;
      this.person.sex = legalRepresentative.sex;
      this.person.evidenceType = legalRepresentative.evidenceType;
      this.person.evidenceNumber = legalRepresentative.evidenceNumber;
      this.person.iban = legalRepresentative.iban;
      this.person.validityOfPermanentResidence = legalRepresentative.validityOfPermanentResidence;
      this.person.validityOfZiadost = legalRepresentative.validityOfZiadost;
      this.person.validityOfDocumentFrom = legalRepresentative.validityOfDocumentFrom;
      this.person.validityOfDocumentTo = legalRepresentative.validityOfDocumentTo;
    },
    resetPersonData() {
      this.person.name = null;
      this.person.surname = null;
      this.person.birthSurname = null;
      this.person.nationality = this.$store.getters.getSlovakia;
      this.person.hasBirthNumber = true;
      this.person.birthNumber = null;
      this.person.birthDate = null;
      this.person.sex = null;
      this.person.evidenceType = null;
      this.person.evidenceNumber = null;
      this.person.iban = null;
      this.person.validityOfPermanentResidence = null;
      this.person.validityOfZiadost = null;
      this.person.validityOfDocumentFrom = null;
      this.person.validityOfDocumentTo = null;
    },
    setLegalRepresentativeIndexForAllChildren(index) {
      this.children.forEach((child) => {
        child.firstLegalRepresentative.personIndex = index;
      });
    },
  },
};
</script>
